import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { PickListModule } from 'primeng/picklist';

// Third Party modules
import { LoggerModule, NgxLoggerLevel, NGXLogger } from 'ngx-logger';
import { OAuthModule } from 'angular-oauth2-oidc';

import { AppComponent } from './app.component';
import { ErrorNotFoundComponent } from './errors/error-notfound/error-notfound.component';
import { ErrorNoStudyAccessComponent } from './errors/error-nostudyaccess/error-nostudyaccess.component';
import { ErrorPrivacyNoticeComponent } from './errors/error-privacynotice/error-privacynotice.component';
import { LoggedOutComponent } from './auth/logged-out/logged-out.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { UserLoginLogoutComponent } from '@shared/user-login-logout/user-login-logout.component';
import { NotificationIndicatorComponent } from '@shared/notification-indicator/notification-indicator.component';
import { AppRoutingModule } from './app-routing.module';

import { SharedModule } from './shared.module';
import { DialogComponent } from '@shared/dialog/dialog.component';
import { DialogNotificationsComponent } from '@shared/dialog-notifications/dialog-notifications.component';
import { AddFilterDialogComponent } from './mystudies/add-filter-dialog/add-filter-dialog.component';
import { NewStudyDialogComponent } from './mystudies/newstudy-dialog/newstudy-dialog.component';
import { InternetReviewScrapeDialogComponent } from './mystudies/analysis/internetreview/internet-review-scrape-dialog/internet-review-scrape-dialog.component';
import { ReasonDialogComponent } from '@shared/reason-dialog/reason-dialog.component';
import { AddKeywordsDialogComponent } from './mystudies/analysis/keyword-screening/add-keywords-dialog/add-keywords-dialog.component';

// Guards
import { AuthGuard } from '@services/auth.guard';
import { CanDeactivateGuard } from '@services/candeactivate.guard';

// Services that are used in more than one area
import { LookupService } from '@services/lookup.service';
import { CommonDataServicesService } from '@services/common-data-services.service';
import { StudyService } from '@services/study.service';
import { DialogService } from '@services/dialog.service';
import { LoggingService } from '@services/logging.service';
import { UserService } from '@services/user.service';
import { AuthService } from '@services/auth.service';
import { WebAPIService } from '@services/webapi.service';
import { GraphService } from '@services/graph.service';
import { MonitoringService } from '@services/monitoring.service';
import { ConfigService } from '@services/config.service';
import { UserNotificationService } from '@services/user-notification.service';
// Used to handle http requests and modify them before they are sent out
import { HttpRequestInterceptor } from '@services/httprequest.interceptor';
import { TreeViewService } from '@services/treeview.service';
import { PrivacyNoticeDialogComponent } from './privacynotice/privacynotice-dialog.component';
import { PrivacyNoticeComponent } from './privacynotice/privacynotice.component';
import { MonitoringErrorHandler } from '@services/monitoring.errorhandler';
import { EliminationCriteriaDialogComponent } from './mystudies/analysis/business-description-review/elimination-matrix/criteria-dialog/elimination-criteria-dialog.component';
import { StudyCommentService } from '@services/study-comments.service';
import { SubsetDialogComponent } from './mystudies/analysis/business-description-review/elimination-matrix/subset-dialog/subset-dialog.component';
import { StudyDetailService } from '@services/study-detail.service';
import { DataDisplayDialogComponent } from './mystudies/analysis/business-description-review/comparable-details/data-display-dialog.component';
import { StudyReportsModalComponent } from './mystudies/study-reports/study-reports-modal/study-reports-modal.component';
import { FinancialAdjustmentsExportModalComponent } from './mystudies/analysis/financial-adjustments-export-modal/financial-adjustments-export-modal.component';
import { FinancialAdjustmentsImportModalComponent } from './mystudies/analysis/master-matrix/import-modal/financial-adjustments-import-modal.component';
import { ImportMatrixModalComponent } from './mystudies/analysis/master-matrix/import-matrix-modal/import-matrix-modal.component';
import { InternalNotesModalComponent } from './mystudies/analysis/master-matrix/internalnotes-modal/internalnotes-modal.component';
import { TooltipService } from './tooltip/tooltip.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Pipes
import { SortDatePipe } from '@shared/pipes/sort-date.pipe';
import { NewStudyNoticeComponent } from './mystudies/new-study-notice/new-study-notice.component';
import { ScreenVersionNoticeComponent } from './mystudies/screen-version-notice/screen-version-notice.component';
import { RouterOutlet } from '@angular/router';
import { ActiveComponentService } from '@services/active-component.service';
import { FinancialTypeInfoDialogComponent } from './mystudies/financialtype-info-dialog/financialtype-info-dialog.component';
import { ValidateBvdIdService } from '@services/validate-bvd-id/validate-bvd-id.service';
import { BvdIdsLimitDirective } from './mystudies/add-filter-dialog/bvd-ids-limit/bvd-ids-limit.directive';
import { NavigationService } from '@services/navigation.service';
import { ServiceNowService } from '@services/servicenow.service';
import { StudyBlockUiService } from '@shared/study-block-ui/study-block-ui.service';
import { InternetReviewSelectionService } from './mystudies/analysis/internetreview/internet-review-selection.service';
import { PageTitlesRetrievalSelectionService } from './mystudies/analysis/internetreview/page-titles-retrieval-selection.service';
import { ScraperProgressService } from './mystudies/analysis/internetreview/scraper-progress/scraper-progress.service';
import { ScraperProgressDialogService } from './mystudies/analysis/internetreview/scraper-progress-dialog/scraper-progress-dialog.service';
import { ScraperProgressDialogComponent } from './mystudies/analysis/internetreview/scraper-progress-dialog/scraper-progress-dialog.component';
import { KeywordScreeningInfoDialogComponent } from './mystudies/study-strategy-results/keyword-screening-info-dialog/keyword-screening-info-dialog.component';
import { StaticTooltipsService } from './mystudies/analysis/static-tooltips.service';
import { MessageService } from 'primeng/api';
import { ToastNotificationsComponent } from '@shared/toast-notifications/toast-notifications.component';
import { ChangeNotificationsService } from './mystudies/change-notifications.service';
import { TabViewSwitchService } from './mystudies/tab-view-switch.service';
import { SearchStrategyNotificationService } from './mystudies/analysis/search-strategy/search-strategy-notification.service';
import { FinancialItemDbSourceService } from './mystudies/analysis/financialadjustments/financial-review-view/financial-item-db-source.service';

import { FilterCategoryOfCompanyComponent } from './mystudies/add-filter-dialog/filter-category-of-company/filter-category-of-company.component';
import { FilterIdentificationNumberComponent } from './mystudies/add-filter-dialog/filter-identification-number/filter-identification-number.component';
import { FilterStatusComponent } from './mystudies/add-filter-dialog/filter-status/filter-status.component';
import { FilterWebsiteComponent } from './mystudies/add-filter-dialog/filter-website/filter-website.component';

import { FilterBVDIndependenceIndicatorComponent } from './mystudies/add-filter-dialog/filter-bvdindependence-indicator/filter-bvdindependence-indicator.component';
import { FilterShareholderCharacteristicsComponent } from './mystudies/add-filter-dialog/filter-shareholder-characteristics/filter-shareholder-characteristics.component';
import { FilterSubsidiaryCharacteristicsComponent } from './mystudies/add-filter-dialog/filter-subsidiary-characteristics/filter-subsidiary-characteristics.component';
import { FilterFinancialComponent } from './mystudies/add-filter-dialog/filter-financial/filter-financial.component';

import { FilterYearOfIncorporationComponent } from './mystudies/add-filter-dialog/filter-year-of-incorporation/filter-year-of-incorporation.component';
import { FilterLegalFormComponent } from './mystudies/add-filter-dialog/filter-legal-form/filter-legal-form.component';
import { FilterLocationComponent } from './mystudies/add-filter-dialog/filter-location/filter-location.component';
import { FilterAccountTypesComponent } from './mystudies/add-filter-dialog/filter-account-types/filter-account-types.component';
import { FilterCompanyNameComponent } from './mystudies/add-filter-dialog/filter-company-name/filter-company-name.component';
import { FilterNumberOfEmployeesComponent } from './mystudies/add-filter-dialog/filter-number-of-employees/filter-number-of-employees.component';
import { FilterIndustryClassificationsComponent } from './mystudies/add-filter-dialog/filter-industry-classifications/filter-industry-classifications.component';
import { FilterKeywordsComponent } from './mystudies/add-filter-dialog/filter-keywords/filter-keywords.component';
import { ADJUSTMENT_ENTRY_COMPONENTS } from './mystudies/analysis/adjustments';

import { BvdDisplayComponent } from './mystudies/analysis/business-description-review/comparable-details/bvd-display/bvd-display.component';
import { NonBvdDisplayComponent } from './mystudies/analysis/business-description-review/comparable-details/non-bvd-display/non-bvd-display.component';
import { GridService } from './mystudies/analysis/grid.service';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { reducers, metaReducers } from './reducers';
import { StoreRouterConnectingModule, RouterState } from '@ngrx/router-store';
import { environment } from '../environments/environment';
import { KeywordsBooleanLogicValidator } from './mystudies/add-filter-dialog/filter-keywords/filter-keywords-boolean-logic.directive';
import { AppConfigService } from './services/app-config.service';
import { CompanySlideOutComponent } from './mystudies/study-slide-outs/slide-outs/company-slide-out/company-slide-out.component';
import { CompanySlideOutSearchComponent } from './mystudies/study-slide-outs/sections/company-search/company-slide-out-search.component';
import { CompanySlideOutDrillDownComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-slide-out-drill-down.component';
import { CompanyInfoComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/company-info.component';
import { BusinessDescriptionComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/business-description.component';
import { IndustryDescriptionsComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/industry-descriptions.component';
import { FinancialsComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/financials.component';
import { FinancialCategoryComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/financial-category.component';
import { ShareHoldersComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/share-holders.component';
import { SubsidiariesComponent } from './mystudies/study-slide-outs/sections/company-drill-down/company-drill-down-sections/subsidiaries.component';
import { CompanyService } from './mystudies/study-slide-outs/services/company.service';
import { DrillDownSlideOutComponent } from './mystudies/study-slide-outs/slide-outs/drill-down-slide-out/drill-down-slide-out.component';
import { FilterListedUnlistedComponent } from './mystudies/add-filter-dialog/filter-listed-unlisted/filter-listed-unlisted.component';
import { BrowserTabVisibilityChangeService } from './services/browser-tab-visibility-change.service';
import { AvatarModule } from 'primeng/avatar';
import { StudyBydateRangeComponent } from './study-bydate-range/study-bydate-range.component';
import { ReportColumnSelectionModalComponent } from './reports/report-column-selection-modal/report-column-selection-modal.component';
//import { StudyBydateRangeComponent } from './study-bydate-range/study-bydate-range.component';
import { NonceService } from '@services/nonce.service';
import { TimeagoModule } from 'ngx-timeago';

export function appInit(tooltipService: TooltipService) {
  return () => tooltipService.load();
}

export function configLoader(appConfigService: AppConfigService) {
  return () => appConfigService.load();
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorNotFoundComponent,
    ErrorNoStudyAccessComponent,
    ErrorPrivacyNoticeComponent,
    LoggedOutComponent,
    NavMenuComponent,
    UserLoginLogoutComponent,
    NotificationIndicatorComponent,
    DialogComponent,
    DialogNotificationsComponent,
    AddFilterDialogComponent,
    NewStudyDialogComponent,
    PrivacyNoticeDialogComponent,
    PrivacyNoticeComponent,
    DataDisplayDialogComponent,
    EliminationCriteriaDialogComponent,
    SubsetDialogComponent,
    FinancialAdjustmentsExportModalComponent,
    CompanySlideOutComponent,
    DrillDownSlideOutComponent,
    CompanySlideOutSearchComponent,
    CompanySlideOutDrillDownComponent,
    CompanyInfoComponent,
    BusinessDescriptionComponent,
    IndustryDescriptionsComponent,
    FinancialsComponent,
    FinancialCategoryComponent,
    ShareHoldersComponent,
    SubsidiariesComponent,
    StudyReportsModalComponent,
    SortDatePipe,
    FinancialAdjustmentsImportModalComponent,
    ImportMatrixModalComponent,
    InternetReviewScrapeDialogComponent,
    ReasonDialogComponent,
    InternalNotesModalComponent,
    AddKeywordsDialogComponent,
    NewStudyNoticeComponent,
    ScreenVersionNoticeComponent,
    FinancialTypeInfoDialogComponent,
    BvdIdsLimitDirective,
    ScraperProgressDialogComponent,
    KeywordScreeningInfoDialogComponent,
    ToastNotificationsComponent,
    FilterCategoryOfCompanyComponent,
    FilterIdentificationNumberComponent,
    FilterStatusComponent,
    FilterWebsiteComponent,
    FilterBVDIndependenceIndicatorComponent,
    FilterShareholderCharacteristicsComponent,
    FilterSubsidiaryCharacteristicsComponent,
    FilterFinancialComponent,
    FilterYearOfIncorporationComponent,
    FilterLegalFormComponent,
    FilterLocationComponent,
    FilterAccountTypesComponent,
    FilterCompanyNameComponent,
    FilterNumberOfEmployeesComponent,
    FilterIndustryClassificationsComponent,
    FilterKeywordsComponent,
    FilterListedUnlistedComponent,
    ...ADJUSTMENT_ENTRY_COMPONENTS,
    BvdDisplayComponent,
    NonBvdDisplayComponent,
    KeywordsBooleanLogicValidator,
    ReportColumnSelectionModalComponent,
  ],
  imports: [
    HttpClientModule,
    PickListModule,
    OAuthModule.forRoot(),
    FormsModule,
    LoggerModule.forRoot({ level: NgxLoggerLevel.TRACE }),
    SharedModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    EffectsModule.forRoot([]),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      routerState: RouterState.Minimal,
    }),
    AvatarModule,
    ProgressSpinnerModule,
    TimeagoModule.forRoot(),
  ],
  exports: [PickListModule],
  providers: [
    RouterOutlet,
    LookupService,
    CommonDataServicesService,
    StudyService,
    CompanyService,
    StudyDetailService,
    DialogService,
    LoggingService,
    AuthService,
    WebAPIService,
    GraphService,
    MessageService,
    NGXLogger,
    UserService,
    MonitoringService,
    SearchStrategyNotificationService,
    {
      provide: ErrorHandler,
      useClass: MonitoringErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    AuthGuard,
    CanDeactivateGuard,
    TreeViewService,
    StudyCommentService,
    ConfigService,
    AppConfigService,
    UserNotificationService,
    BrowserTabVisibilityChangeService,
    ActiveComponentService,
    ValidateBvdIdService,
    TooltipService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      multi: true,
      deps: [TooltipService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configLoader,
      multi: true,
      deps: [AppConfigService],
    },
    InternetReviewSelectionService,
    NavigationService,
    ServiceNowService,
    NonceService,
    StudyBlockUiService,
    PageTitlesRetrievalSelectionService,
    ScraperProgressService,
    ScraperProgressDialogService,
    StaticTooltipsService,
    ChangeNotificationsService,
    TabViewSwitchService,
    FinancialItemDbSourceService,
    GridService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
